import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

/** Identifier type for public.vwp_calibration_ */
export type VwpCalibrationId = string & { __brand: 'VwpCalibrationId' }

/** Represents the table public.vwp_calibration_ */
export default interface VwpCalibrationTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    VwpCalibrationId,
    VwpCalibrationId | undefined,
    VwpCalibrationId
  >

  /** Database type: pg_catalog.text */
  gaugeSerial: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  sensorSn: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.float8 */
  linearCf: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  tempCorrectionTk: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  polyA: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  polyB: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  polyC: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  rangeKpa: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.date */
  shippedZeroDate: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.float8 */
  shippedZeroReadingB: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  shippedZeroTempC: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  shippedZeroBaroMbar: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.date */
  siteZeroDate: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.float8 */
  siteZeroReadingB: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  siteZeroTempC: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  siteZeroBaroKpa: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  model: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  mfgNumber: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  customer: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.float8 */
  cableLengthM: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  cableMarkings: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  cableType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  thermistorType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.float8 */
  calTemperatureC: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  calBaroMbar: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  technician: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.date */
  calibrationDate: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.bool */
  nistCompliant: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.jsonb */
  rawSheet: ColumnType<unknown | null, unknown | null, unknown | null>

  /** Database type: pg_catalog.text */
  documentKey: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>
}

export type VwpCalibration = Selectable<VwpCalibrationTable>

export type VwpCalibrationInitializer = Insertable<VwpCalibrationTable>

export type VwpCalibrationMutator = Updateable<VwpCalibrationTable>

export const vwpCalibrationId =
  z.string() as unknown as z.Schema<VwpCalibrationId>

export const vwpCalibration = z.object({
  id: vwpCalibrationId,
  gaugeSerial: z.string(),
  sensorSn: z.string().nullable().optional(),
  linearCf: z.number().nullable().optional(),
  tempCorrectionTk: z.number().nullable().optional(),
  polyA: z.number().nullable().optional(),
  polyB: z.number().nullable().optional(),
  polyC: z.number().nullable().optional(),
  rangeKpa: z.number().nullable().optional(),
  shippedZeroDate: z.coerce.date().nullable().optional(),
  shippedZeroReadingB: z.number().nullable().optional(),
  shippedZeroTempC: z.number().nullable().optional(),
  shippedZeroBaroMbar: z.number().nullable().optional(),
  siteZeroDate: z.coerce.date().nullable().optional(),
  siteZeroReadingB: z.number().nullable().optional(),
  siteZeroTempC: z.number().nullable().optional(),
  siteZeroBaroKpa: z.number().nullable().optional(),
  model: z.string().nullable().optional(),
  mfgNumber: z.string().nullable().optional(),
  customer: z.string().nullable().optional(),
  cableLengthM: z.number().nullable().optional(),
  cableMarkings: z.string().nullable().optional(),
  cableType: z.string().nullable().optional(),
  thermistorType: z.string().nullable().optional(),
  calTemperatureC: z.number().nullable().optional(),
  calBaroMbar: z.number().nullable().optional(),
  technician: z.string().nullable().optional(),
  calibrationDate: z.coerce.date().nullable().optional(),
  nistCompliant: z.boolean().nullable().optional(),
  rawSheet: z.unknown().nullable().optional(),
  documentKey: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
}) as unknown as z.Schema<VwpCalibration>

export const vwpCalibrationInitializer = z.object({
  id: vwpCalibrationId.optional(),
  gaugeSerial: z.string(),
  sensorSn: z.string().optional().nullable(),
  linearCf: z.number().optional().nullable(),
  tempCorrectionTk: z.number().optional().nullable(),
  polyA: z.number().optional().nullable(),
  polyB: z.number().optional().nullable(),
  polyC: z.number().optional().nullable(),
  rangeKpa: z.number().optional().nullable(),
  shippedZeroDate: z.coerce.date().optional().nullable(),
  shippedZeroReadingB: z.number().optional().nullable(),
  shippedZeroTempC: z.number().optional().nullable(),
  shippedZeroBaroMbar: z.number().optional().nullable(),
  siteZeroDate: z.coerce.date().optional().nullable(),
  siteZeroReadingB: z.number().optional().nullable(),
  siteZeroTempC: z.number().optional().nullable(),
  siteZeroBaroKpa: z.number().optional().nullable(),
  model: z.string().optional().nullable(),
  mfgNumber: z.string().optional().nullable(),
  customer: z.string().optional().nullable(),
  cableLengthM: z.number().optional().nullable(),
  cableMarkings: z.string().optional().nullable(),
  cableType: z.string().optional().nullable(),
  thermistorType: z.string().optional().nullable(),
  calTemperatureC: z.number().optional().nullable(),
  calBaroMbar: z.number().optional().nullable(),
  technician: z.string().optional().nullable(),
  calibrationDate: z.coerce.date().optional().nullable(),
  nistCompliant: z.boolean().optional().nullable(),
  rawSheet: z.unknown().optional().nullable(),
  documentKey: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<VwpCalibrationInitializer>

export const vwpCalibrationMutator = z.object({
  id: vwpCalibrationId.optional(),
  gaugeSerial: z.string().optional(),
  sensorSn: z.string().optional().nullable(),
  linearCf: z.number().optional().nullable(),
  tempCorrectionTk: z.number().optional().nullable(),
  polyA: z.number().optional().nullable(),
  polyB: z.number().optional().nullable(),
  polyC: z.number().optional().nullable(),
  rangeKpa: z.number().optional().nullable(),
  shippedZeroDate: z.coerce.date().optional().nullable(),
  shippedZeroReadingB: z.number().optional().nullable(),
  shippedZeroTempC: z.number().optional().nullable(),
  shippedZeroBaroMbar: z.number().optional().nullable(),
  siteZeroDate: z.coerce.date().optional().nullable(),
  siteZeroReadingB: z.number().optional().nullable(),
  siteZeroTempC: z.number().optional().nullable(),
  siteZeroBaroKpa: z.number().optional().nullable(),
  model: z.string().optional().nullable(),
  mfgNumber: z.string().optional().nullable(),
  customer: z.string().optional().nullable(),
  cableLengthM: z.number().optional().nullable(),
  cableMarkings: z.string().optional().nullable(),
  cableType: z.string().optional().nullable(),
  thermistorType: z.string().optional().nullable(),
  calTemperatureC: z.number().optional().nullable(),
  calBaroMbar: z.number().optional().nullable(),
  technician: z.string().optional().nullable(),
  calibrationDate: z.coerce.date().optional().nullable(),
  nistCompliant: z.boolean().optional().nullable(),
  rawSheet: z.unknown().optional().nullable(),
  documentKey: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<VwpCalibrationMutator>
